import { Flex, Box, Text } from '@chatsdet/ui';
import type { ExploratoryBrowserSession, ReviewFinding, UxReviewReportJson } from '../types';
import { ReviewFindingsList } from './ReviewFindingsList';

const OUTCOME_LABEL: Record<string, string> = {
  completed: 'Completed',
  completed_with_struggle: 'Completed with struggle',
  blocked: 'Blocked',
};

function gradeTone(grade: string): string {
  const head = grade[0];
  if (head === 'A') return 'text-emerald-600';
  if (head === 'B') return 'text-lime-600';
  if (head === 'C') return 'text-amber-600';
  if (head === 'D') return 'text-orange-600';
  return 'text-red-600';
}

/** Display grades as flat letters (drop any +/- suffix). */
function flatGrade(grade: string): string {
  return grade.charAt(0);
}

// ── Panel ────────────────────────────────────────────────────────────────────

/**
 * UX-review sidebar — the graded headline + severity-grouped findings, each with
 * an icon action bar (confirm / dismiss / file-as-issue) and a confirm dialog
 * when filing. Shown instead of the functional Plan/Report/Issues tabs.
 */
export function UxReviewPanel({
  report,
  findings,
  runId,
  sessions,
  onSelectSession,
}: {
  report: UxReviewReportJson | null;
  findings: ReviewFinding[];
  runId: string;
  sessions: ExploratoryBrowserSession[];
  onSelectSession: (id: string) => void;
}) {
  const dimensionLabel = (key: string) => report?.dimensions.find((d) => d.key === key)?.label;

  return (
    <Box className="flex h-full flex-col overflow-y-auto">
      {report && (
        <Box className="border-b border-border p-4">
          <Flex align="center" gap="md">
            <span
              className={`text-5xl font-bold leading-none tracking-tight ${gradeTone(
                report.overallGrade
              )}`}
            >
              {flatGrade(report.overallGrade)}
            </span>
            <div className="min-w-0 flex-1">
              <Text size="xs" className="uppercase tracking-wide text-muted-foreground">
                UX review
              </Text>
              <Text size="md" weight="semibold" className="leading-snug text-foreground">
                {OUTCOME_LABEL[report.goalOutcome] ?? report.goalOutcome}
              </Text>
            </div>
          </Flex>

          {report.summary && (
            <Text size="sm" className="mt-3 leading-snug text-muted-foreground">
              {report.summary}
            </Text>
          )}

          <div className="mt-4 grid grid-cols-4 gap-2">
            {report.dimensions.map((d) => (
              <Box key={d.key}>
                <Text size="xs" className="truncate text-muted-foreground">
                  {d.label}
                </Text>
                <Text size="sm" weight="bold" className={gradeTone(d.grade)}>
                  {flatGrade(d.grade)}
                </Text>
              </Box>
            ))}
          </div>
        </Box>
      )}

      <ReviewFindingsList
        findings={findings}
        runId={runId}
        sessions={sessions}
        onSelectSession={onSelectSession}
        dimensionLabel={dimensionLabel}
        dimensionOrder={report?.dimensions.map((d) => d.key) ?? []}
      />
    </Box>
  );
}
